import { type Editor, useEditor, EditorContent } from '@tiptap/react';
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MediaUploader } from "@/components/media/MediaUploader";
import { useTable } from "@refinedev/core";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Code,
  Unlink,
  Search,
  FileImage,
  Upload,
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

interface MediaFile {
  id: number;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
}

interface TableResult {
  tableQueryResult?: {
    data?: {
      data?: MediaFile[];
    };
    isLoading?: boolean;
  };
  setFilters: (
    filters: Array<{ field: string; operator: string; value: string }>
  ) => void;
}

/**
 * Кнопка тулбара редактора
 */
function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant={active ? "secondary" : "ghost"}
      size="sm"
      className={cn("h-8 w-8 p-0", active && "bg-muted")}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </Button>
  );
}

/**
 * Модальное окно для добавления/редактирования ссылки
 */
interface LinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string) => void;
  onRemove: () => void;
  initialUrl: string;
  hasLink: boolean;
}

function LinkModal({
  open,
  onOpenChange,
  onSubmit,
  onRemove,
  initialUrl,
  hasLink,
}: LinkModalProps) {
  const [url, setUrl] = useState(initialUrl);

  const handleRemove = useCallback(() => {
    onRemove();
    onOpenChange(false);
  }, [onRemove, onOpenChange]);

  // Синхронизация initialUrl при открытии модалки
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        setUrl(initialUrl);
      }
      onOpenChange(newOpen);
    },
    [initialUrl, onOpenChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && url.trim()) {
        e.preventDefault();
        onSubmit(url.trim());
        onOpenChange(false);
      }
    },
    [url, onSubmit, onOpenChange]
  );

  const handleAddClick = useCallback(() => {
    if (url.trim()) {
      onSubmit(url.trim());
      onOpenChange(false);
    }
  }, [url, onSubmit, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{hasLink ? "Редактировать ссылку" : "Добавить ссылку"}</DialogTitle>
          <DialogDescription>
            Введите URL-адрес для создания ссылки на выделенный текст
          </DialogDescription>
        </DialogHeader>
        <div>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="link-url">URL</Label>
              <Input
                id="link-url"
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {hasLink && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleRemove}
              >
                <Unlink className="h-4 w-4 mr-2" />
                Удалить ссылку
              </Button>
            )}
            <Button type="button" onClick={handleAddClick} disabled={!url.trim()}>
              {hasLink ? "Обновить" : "Добавить"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Результат выбора изображения
 */
interface ImageSelectResult {
  url: string;
  alt?: string;
  fileId?: number;
}

/**
 * Модальное окно для выбора/загрузки изображения
 */
interface ImageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (result: ImageSelectResult) => void;
}

function ImageModal({ open, onOpenChange, onSelect }: ImageModalProps) {
  const [tab, setTab] = useState<"library" | "upload" | "url">("library");
  const [urlInput, setUrlInput] = useState("");
  const [altInput, setAltInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { tableQueryResult, setFilters } = useTable({
    resource: "files/admin",
    pagination: { pageSize: 12 },
    syncWithLocation: false,
    filters: {
      initial: [
        { field: "mimeType", operator: "contains", value: "image" },
      ],
    },
    queryOptions: {
      enabled: open,
    },
  }) as TableResult;

  const files = tableQueryResult?.data?.data || [];
  const isLoading = tableQueryResult?.isLoading ?? false;

  const handleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setFilters([
        { field: "search", operator: "contains", value },
        { field: "mimeType", operator: "contains", value: "image" },
      ]);
    },
    [setFilters]
  );

  const resetState = useCallback(() => {
    setTab("library");
    setUrlInput("");
    setAltInput("");
    setSearchQuery("");
  }, []);

  const handleFileSelect = useCallback(
    (file: MediaFile) => {
      onSelect({
        url: file.url,
        alt: file.originalName,
        fileId: file.id,
      });
      onOpenChange(false);
      resetState();
    },
    [onSelect, onOpenChange, resetState]
  );

  const handleUploadComplete = useCallback(
    (file: { id?: number | string; url: string; originalName: string }) => {
      onSelect({
        url: file.url,
        alt: file.originalName,
        fileId: file.id ? Number(file.id) : undefined,
      });
      onOpenChange(false);
      resetState();
    },
    [onSelect, onOpenChange, resetState]
  );

  const handleUrlInsert = useCallback(() => {
    if (urlInput.trim()) {
      onSelect({
        url: urlInput.trim(),
        alt: altInput.trim() || undefined,
      });
      onOpenChange(false);
      resetState();
    }
  }, [urlInput, altInput, onSelect, onOpenChange, resetState]);

  const handleUrlKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && urlInput.trim()) {
        e.preventDefault();
        handleUrlInsert();
      }
    },
    [urlInput, handleUrlInsert]
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetState();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, resetState]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Выбрать изображение</DialogTitle>
          <DialogDescription>
            Выберите изображение из библиотеки, загрузите новое или укажите URL
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "library" | "upload" | "url")}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="library" className="gap-2">
              <FileImage className="h-4 w-4" />
              Библиотека
            </TabsTrigger>
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="h-4 w-4" />
              Загрузить
            </TabsTrigger>
            <TabsTrigger value="url" className="gap-2">
              <LinkIcon className="h-4 w-4" />
              По URL
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="library"
            forceMount
            className="flex-1 overflow-hidden flex flex-col gap-4 mt-4 data-[state=inactive]:hidden"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск изображений..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex-1 overflow-auto grid grid-cols-2 md:grid-cols-4 gap-4 p-1">
              {isLoading ? (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <div className="h-12 w-12 mb-4 border-4 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
                  <p className="text-sm">Загрузка изображений...</p>
                </div>
              ) : (files.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FileImage className="h-12 w-12 mb-4" />
                  <p className="text-sm">Изображения не найдены</p>
                  <p className="text-xs mt-1">
                    Попробуйте изменить поисковый запрос или загрузите новое изображение
                  </p>
                </div>
              ) : (
                files.map((file) => (
                  <div
                    key={file.id}
                    className="group relative border rounded-lg overflow-hidden cursor-pointer hover:border-primary hover:ring-2 hover:ring-primary/20 transition-all"
                    onClick={() => handleFileSelect(file)}
                  >
                    <div className="aspect-square w-full bg-muted overflow-hidden">
                      <img
                        src={file.url}
                        alt={file.originalName}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p
                        className="text-xs text-white truncate"
                        title={file.originalName}
                      >
                        {file.originalName}
                      </p>
                    </div>
                  </div>
                ))
              ))}
            </div>
          </TabsContent>

          <TabsContent value="upload" forceMount className="mt-4 data-[state=inactive]:hidden">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Перетащите изображение или нажмите для выбора файла
              </p>
              <MediaUploader onUploadComplete={handleUploadComplete} />
            </div>
          </TabsContent>

          <TabsContent value="url" forceMount className="mt-4 data-[state=inactive]:hidden">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="image-url">URL изображения</Label>
                <Input
                  id="image-url"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={handleUrlKeyDown}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image-alt">
                  Alt-текст{" "}
                  <span className="text-muted-foreground">(необязательно)</span>
                </Label>
                <Input
                  id="image-alt"
                  type="text"
                  placeholder="Описание изображения"
                  value={altInput}
                  onChange={(e) => setAltInput(e.target.value)}
                />
              </div>
              {urlInput && (
                <div className="border rounded-lg p-4 bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-2">Предпросмотр:</p>
                  <img
                    src={urlInput}
                    alt={altInput || "Preview"}
                    className="max-h-48 rounded object-contain mx-auto"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              <DialogFooter>
                <Button type="button" onClick={handleUrlInsert} disabled={!urlInput.trim()}>
                  Вставить изображение
                </Button>
              </DialogFooter>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Тулбар редактора
 */
function Toolbar({ editor }: { editor: Editor }) {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const handleLinkSubmit = useCallback(
    (url: string) => {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    },
    [editor]
  );

  const handleLinkRemove = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
  }, [editor]);

  const handleImageSelect = useCallback(
    (result: ImageSelectResult) => {
      editor.chain().focus().setImage({ src: result.url, alt: result.alt ?? "" }).run();
    },
    [editor]
  );

  const currentLinkUrl =
    (editor.getAttributes("link").href as string | undefined) ?? "";
  const hasLink = editor.isActive("link");

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 border-b p-2 bg-muted/50">
        {/* История */}
        <div className="flex items-center gap-0.5 mr-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Отменить (Ctrl+Z)"
          >
            <Undo className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Повторить (Ctrl+Y)"
          >
            <Redo className="h-4 w-4" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-border" />

        {/* Заголовки */}
        <div className="flex items-center gap-0.5 mx-2">
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            active={editor.isActive("heading", { level: 1 })}
            title="Заголовок 1"
          >
            <Heading1 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            active={editor.isActive("heading", { level: 2 })}
            title="Заголовок 2"
          >
            <Heading2 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            active={editor.isActive("heading", { level: 3 })}
            title="Заголовок 3"
          >
            <Heading3 className="h-4 w-4" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-border" />

        {/* Форматирование текста */}
        <div className="flex items-center gap-0.5 mx-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Жирный (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="Курсив (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCode().run()}
            active={editor.isActive("code")}
            title="Код"
          >
            <Code className="h-4 w-4" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-border" />

        {/* Списки */}
        <div className="flex items-center gap-0.5 mx-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Маркированный список"
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Нумерованный список"
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive("blockquote")}
            title="Цитата"
          >
            <Quote className="h-4 w-4" />
          </ToolbarButton>
        </div>

        <div className="w-px h-6 bg-border" />

        {/* Медиа */}
        <div className="flex items-center gap-0.5 mx-2">
          <ToolbarButton
            onClick={() => setLinkModalOpen(true)}
            active={hasLink}
            title="Ссылка (Ctrl+K)"
          >
            <LinkIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => setImageModalOpen(true)}
            title="Изображение"
          >
            <ImageIcon className="h-4 w-4" />
          </ToolbarButton>
        </div>
      </div>

      <LinkModal
        open={linkModalOpen}
        onOpenChange={setLinkModalOpen}
        onSubmit={handleLinkSubmit}
        onRemove={handleLinkRemove}
        initialUrl={currentLinkUrl}
        hasLink={hasLink}
      />

      <ImageModal
        open={imageModalOpen}
        onOpenChange={setImageModalOpen}
        onSelect={handleImageSelect}
      />
    </>
  );
}

/**
 * WYSIWYG-редактор на базе TipTap
 *
 * Поддерживает:
 * - Заголовки (H1, H2, H3)
 * - Форматирование текста (жирный, курсив, код)
 * - Списки (маркированные, нумерованные)
 * - Цитаты
 * - Ссылки (с модальным окном)
 * - Изображения (выбор из библиотеки, загрузка, URL)
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder = "Начните вводить текст...",
  className,
  disabled = false,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        link: {
          openOnClick: false,
          HTMLAttributes: {
            class: "text-primary underline cursor-pointer",
          },
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "rounded-md max-w-full h-auto",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        // text-foreground привязан к CSS-переменной темы (тёмный текст на светлой
        // теме, светлый — на тёмной). Плагин @tailwindcss/typography не подключён,
        // поэтому prose-классы не давали цвета и текст наследовал бледный цвет —
        // нечитаемый на белом фоне. Задаём цвет явно.
        class:
          "prose prose-sm max-w-none min-h-[200px] p-4 focus:outline-none text-foreground",
      },
    },
  });

  if (!editor) {
    return (
      <div
        className={cn(
          "rounded-md border bg-muted/50 min-h-[200px] animate-pulse",
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border bg-background",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {!disabled && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className={cn(
          "rich-text-editor",
          "[&_.ProseMirror]:min-h-[200px]",
          "[&_.ProseMirror]:p-4",
          "[&_.ProseMirror]:focus:outline-none",
          "[&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-4",
          "[&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mb-3",
          "[&_.ProseMirror_h3]:text-lg [&_.ProseMirror_h3]:font-medium [&_.ProseMirror_h3]:mb-2",
          "[&_.ProseMirror_p]:mb-2",
          "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ul]:mb-2",
          "[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_ol]:mb-2",
          "[&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-muted-foreground/30 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0",
          "[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
        )}
      />
    </div>
  );
}
